import { useState, useMemo } from "react";
import { LicenseManager } from "ag-charts-enterprise";
import {
    AGGRID_LICENCE_KEY,
    Utils,
    LoaderComponent,
    OmniTooltipNextComponent,
    OmniIconComponent,
} from "../../../../../shared";
import "ag-charts-community";
import "ag-charts-enterprise";
import { AgCharts } from "ag-charts-react";

LicenseManager.setLicenseKey(AGGRID_LICENCE_KEY);

const OrderProgressCard = ({ plannedAmount = 0, mohOrderedAmount = 0 , orderedAmount = 0,isLocalCurrency, orderedData=null }) => {
    let isLoading = true;
    if (Array.isArray(orderedData) && orderedData.length === 0) {
        // Empty array - still loading
        isLoading = true;
    } else if (orderedData && typeof orderedData === 'object' && Object.keys(orderedData).length > 0) {
        // Object with values - done loading
        isLoading = false;
    } else if (orderedData == null) {
        // null or undefined - done loading
        isLoading = false;
    }
    // const [shouldIncludeNonDigital] = useState(false);

    const progressPercentage = useMemo(() => {
        const planned = plannedAmount ;
        return planned && planned > 0
            ? (orderedAmount / planned) * 100
            : 0;
    }, [plannedAmount, orderedAmount, mohOrderedAmount]);

    const formatYAxisLabel = (value) => {
        if (value >= 1000000) return `${(value / 1000000).toFixed(0)}M`;
        if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
        return value.toString();
    };

    const chartOptions = useMemo(
        () => ({
            data: [
                {
                    category: "Planned",
                    value: Number(plannedAmount) 
                },
                {
                    category: "Ordered (MOH)",
                    value: Number(mohOrderedAmount),
                },
                {
                    category: "Ordered (Prisma)",
                    value: Number(orderedAmount),
                }
            ],
            series: [
                {
                    type: "bar",
                    xKey: "category",
                    yKey: "value",
                    cornerRadius: 5,
                    itemStyler: (params) => {
                        const isMOHOrdered = params.datum.category === "Ordered (MOH)";
                        const isOrdered = params.datum.category === "Ordered (Prisma)";
                        return {
                            fill: isMOHOrdered ? "#96F5E6" : isOrdered ? "#2DD4BF" : "#D1D5DB",
                            stroke: isMOHOrdered ? "#96F5E6" : isOrdered ? "#2DD4BF" : "#D1D5DB",
                        };
                    },
                    tooltip: {
                        enabled: true,
                        renderer: (params) => {
                            const isPlanned = params.datum.category === 'Planned';
                            const isMOHOrdered = params.datum.category === "Ordered (MOH)";
                            const isOrdered = params.datum.category === "Ordered (Prisma)";
                            const totalPlanned = plannedAmount ;
                            return isPlanned 
                                ? `<div style="padding: 8px;">Total planned: <b>${Utils.getLocalCurrency(isLocalCurrency.currencyName,totalPlanned)}</b></div>`:
                                isMOHOrdered
                                    ? `<div style="padding: 8px;">Total ordered (MOH): <b>${Utils.getLocalCurrency(isLocalCurrency.currencyName,mohOrderedAmount)}</b><br/>Budget remaining: ${Utils.getLocalCurrency(isLocalCurrency.currencyName,totalPlanned - mohOrderedAmount)}</div>`
                                : `<div style="padding: 8px;">Total ordered (Prisma): <b>${Utils.getLocalCurrency(isLocalCurrency.currencyName,orderedAmount)}</b><br/>Budget remaining: ${Utils.getLocalCurrency(isLocalCurrency.currencyName,totalPlanned - orderedAmount)}</div>`;
                        }
                    }
                },
            ],
            axes: [
                {
                    type: "category",
                    position: "bottom",
                    line: {
                        enabled: true,
                        color: "#E5E7EB",
                    },
                    gridLine: {
                        enabled: false,
                    },
                    label: {
                        rotation: -45,
                        color: "#6B7280",
                        fontSize: 12,
                        fontStyle: "italic",
                    },
                    paddingInner: 0.5,
                    paddingOuter: 0.2,
                },
                {
                    type: "number",
                    position: "left",
                    line: {
                        enabled: false,
                    },
                    label: {
                        enabled: true,
                        color: "#9CA3AF",
                        fontSize: 11,
                        formatter: (params) => formatYAxisLabel(params.value),
                    },
                    gridLine: {
                        style: [{ stroke: '#E5E7EB', lineDash: [4, 4] }],
                    },
                    title: {
                        enabled: true,
                        text: "USD $",
                        color: "#9CA3AF",
                        fontSize: 11,
                    },
                },
            ],
            legend: {
                enabled: false,
            },
            padding: {
                top: 10,
                right: 20,
                bottom: 10,
                left: 10,
            },
        }),
        [plannedAmount, orderedAmount, mohOrderedAmount]
    );
   
    return (
        isLoading ? (
            <div className="order-progress-card chart-loader" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                <LoaderComponent spinnerCount={1} />
            </div>
        ) :
        <div className="order-progress-card">
            <div className="card-header-section">
                <h3 className="progress-title">Order Progress</h3>
                <div className="percentage-value">
                    <span className="total-percentage" style={{ color: orderedAmount > plannedAmount ? '#9B373C' : '#1A8978' }}>
                        {progressPercentage.toFixed(0)}%
                    </span>
                    {orderedAmount > plannedAmount && (
                        <>
                            <span className="excess-percentage">
                                +{((orderedAmount - plannedAmount) / plannedAmount * 100).toFixed(1)}%
                            </span>
                            <OmniTooltipNextComponent style={{ width: "fit-content" }}>
                                <span slot="invoker" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                        <path d="M11.8854 10.4713L6.88709 0.558258C6.80237 0.389883 6.67517 0.248879 6.51925 0.150488C6.36332 0.0520964 6.1846 5.90925e-05 6.0024 0C5.8202 5.90925e-05 5.64148 0.0520964 5.48556 0.150488C5.32963 0.248879 5.20243 0.389883 5.11771 0.558258L0.119452 10.4713C0.0362291 10.6326 -0.00481491 10.814 0.000449631 10.9972C0.00571417 11.1804 0.0571016 11.3589 0.149442 11.5147C0.240527 11.6649 0.366848 11.7883 0.516426 11.8732C0.666005 11.9581 0.833884 12.0017 1.00414 11.9999H11.0007C11.1727 11.9999 11.3419 11.9535 11.4917 11.8652C11.6415 11.7769 11.767 11.6498 11.856 11.496C11.945 11.3423 11.9944 11.1672 11.9996 10.9877C12.0047 10.8081 11.9654 10.6303 11.8854 10.4713ZM5.50258 4.17389C5.50258 4.03552 5.55524 3.90282 5.64897 3.80497C5.74271 3.70713 5.86984 3.65216 6.0024 3.65216C6.13496 3.65216 6.2621 3.70713 6.35583 3.80497C6.44957 3.90282 6.50223 4.03552 6.50223 4.17389V7.30432C6.50223 7.44269 6.44957 7.57539 6.35583 7.67324C6.2621 7.77108 6.13496 7.82605 6.0024 7.82605C5.86984 7.82605 5.74271 7.77108 5.64897 7.67324C5.55524 7.57539 5.50258 7.44269 5.50258 7.30432V4.17389ZM6.02739 10.1739C5.83053 10.1727 5.64169 10.0923 5.50017 9.94944C5.35866 9.80658 5.27537 9.61225 5.26766 9.40691C5.26432 9.30378 5.28081 9.20098 5.31618 9.10461C5.35154 9.00824 5.40505 8.92025 5.47355 8.84583C5.54204 8.77142 5.62413 8.71209 5.71497 8.67135C5.8058 8.63061 5.90354 8.6093 6.0024 8.60866C6.19927 8.60984 6.38811 8.69023 6.52962 8.83309C6.67114 8.97595 6.75442 9.17027 6.76214 9.37561C6.76548 9.47875 6.74898 9.58154 6.71362 9.67791C6.67825 9.77428 6.62474 9.86228 6.55625 9.93669C6.48775 10.0111 6.40566 10.0704 6.31483 10.1112C6.22399 10.1519 6.12626 10.1732 6.02739 10.1739Z" fill="#FED882"/>
                                    </svg>
                                    {/* <OmniIconComponent className="is-size-4" icon-id="omni:informative:alertDark" style={{ color: '#f0ad4e' }} /> */}
                                </span>
                                <div slot="content">Your ordered amount is higher than your planned amount. Please check with your team to make sure your orders align with the approved budget.</div>
                            </OmniTooltipNextComponent>
                        </>
                    )}
                </div>
                <div className="amount-label">
                    <span style={{ color: orderedAmount > plannedAmount ? '#9B373C' : '' }}>
                        {Utils.getLocalCurrency(isLocalCurrency.currencyName,orderedAmount)}
                    </span>
                    /
                    <span>
                        {Utils.getLocalCurrency(isLocalCurrency.currencyName,plannedAmount)}
                    </span>
                </div>
            </div>
            <div className="chart-section">
                <AgCharts options={chartOptions} />
            </div>
        </div>
    );
};

export default OrderProgressCard;
